<template lang="pug">
.FakturaBlock(v-if="fields" data-qa="faktura-block-container")
  h3(class="font-medium text-[24px] leading-[32px] pt-8 pb-[6px]") {{ $t('faktura_title') }}
  span(class="text-[#646D74] text-base leading-5 tracking-normal align-middle") {{ $t('faktura_receive') }}
  label.switch(v-if="isCompanyFieldVisual")
    input.switch__checkbox(
      type="checkbox"
      data-qa="faktura-company-switch"
      v-model="_isCompany"
      @change="updateCompanyState"
    )
    span.switch__track
      span.switch__thumb
    span.switch__label {{ $t('faktura_legal_entity') }}
  div(v-if="!isCompany && !isPickPoint" class="mt-[10px] w-full")
    div(class="flex items-center pt-[22px] pb-[10px] checkbox-labeled")
      input(
        type="checkbox", 
        v-model="_sameAddress",
        :checked="sameAddress",
        :disabled="isSameCheckboxDisabled",
        class="cursor-pointer checkout-checkbox"
        id="sameAddress"
        data-qa="faktura-same-address-checkbox"
        @change="setFieldsAsSame"
      )
      label(for="sameAddress", class="ml-2 cursor-pointer")
        span(v-html="$t('faktura_same_address')")
  div(
    v-if="!_sameAddress"
    class="mt-[10px] w-full grid lg1024:grid-cols-4 grid-cols-2 gap-4 max-lg1024:grid-cols-1 pb-8"
  )
    template(v-for="field in additionalFields" :key="field.name")
      div(v-if="field.visual" class="flex flex-col justify-end")
        label(:for="field.name.toLowerCase()")
          | {{ field.label }}
          span.required *
        input(
        :id="field.name.toLowerCase()"
        class="border border-gray-2 p-3 mt-1 input-wrap"
        :class="{ 'faktura-error': errorInUserField(field.name)?.length }"
        autocomplete="off"
        :minlength="field.min"
        :maxlength="field.max"
        :type="field.type"
        :data-qa="'faktura-input-' + field.name"
        @input="onInput($event, field)"
        v-model="modelValue[field.name]"
        )
        p.error-text.mt-1.text-sm.text-accentColor(:data-qa="'faktura-error-' + field.name" :class="{ 'error-text--is-visible': !!errorInUserField(field.name)?.length }") {{ errorInUserField(field.name) || '&nbsp;' }}

  .summary(v-else-if="sameAddress" data-qa="faktura-same-address-summary")
    .summary__person
      .summary__name {{ userInfo.FIO}}
      .summary__address {{ getAddress() }}
      .summary__phone {{ userInfo.PHONE}}
</template>

<script>
import LocationProfiles from '@/components/svg/LocationProfiles.vue'
import { createAddress } from '@/global/createAddress'
import { mapState, mapGetters, mapMutations } from 'vuex'

export default {
  name: 'FakturaBlock',
  components: { LocationProfiles },
  props: {
    isShow: {
      type: Boolean,
      default: false,
    },
    fields: {
      type: Array,
      required: false,
    },
    modelValue: {
      type: Object,
    },
    userInfo: {
      type: Object,
      default: () => {},
    },
    profile: {
      type: Object,
      default: () => {},
    },
    isPickPoint: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState(['isCompany', 'sameAddress']),
    ...mapGetters(['userAdditionalFields']),
    _isCompany: {
      get() {
        return this.isCompany
      },
      set(val) {
        this.setIsCompany(val)
      },
    },
    _sameAddress: {
      get() {
        return this.sameAddress
      },
      set(val) {
        this.setAddressAsSame(val)
      },
    },
    isSameCheckboxDisabled() {
      return (
        !this.profile.ADDRESS || !this.profile.POSTINDEX || !this.profile.CITY
      )
    },
    additionalFields() {
      const fields = this.fields.filter((v) => {
        if (this.isCompany) {
          return v.name !== 'FAKTURA_FIO' && v.name !== 'IS_COMPANY'
        } else {
          return (
            v.name !== 'FAKTURA_COMPANY' &&
            v.name !== 'FAKTURA_INN' &&
            v.name !== 'IS_COMPANY'
          )
        }
      })

      return fields
    },
    isCompanyFieldVisual() {
      const isCompanyField = this.fields.find((v) => v.name === 'IS_COMPANY')

      return isCompanyField?.visual
    },
    errorInUserField() {
      return (fieldName) => {
        return this.userAdditionalFields[fieldName]
      }
    },
  },
  methods: {
    ...mapMutations([
      'updateFieldsRequirement',
      'setIsCompany',
      'setAddressAsSame',
    ]),
    setFieldsAsSame() {
      this.additionalFields.forEach((field) =>
        this.$emit('setAdditionalFieldsAsSame', field.name)
      )
    },
    getAddress() {
      return createAddress(
        this.profile,
        this.$store.state.country,
        this.$t,
        true
      )
    },
    updateCompanyState() {
      this.updateFieldsRequirement()
      this.additionalFields.forEach((field) => {
        this.$emit(
          'setValidateValues',
          'ADDITIONAL_FIELDS',
          field.name,
          this.modelValue[`${field.name}`]
        )
      })
      if (this.sameAddress) {
        this.setAddressAsSame(false)
      }
    },
    onInput(event, field) {
      this.$emit(
        'setValidateValues',
        'ADDITIONAL_FIELDS',
        field.name,
        event.target.value
      )
    },
  },
  mounted() {
    this.updateFieldsRequirement()
  },
}
</script>

<style lang="scss">
.switch {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 48px;
  font-family: 'Rubik', sans-serif;
  margin-top: 22px;
  padding-top: 12px;

  @media (max-width: 640px) {
    flex-direction: row-reverse;
    justify-content: space-between;
  }

  &__checkbox {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  &__track {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 24px;
    background-color: #e5e7eb;
    border-radius: 9999px;
    transition: background-color 0.3s;
    cursor: pointer;
  }

  &__thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 20px;
    height: 20px;
    background: #fff;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    transition: transform 0.3s;
  }

  &__checkbox:checked + &__track {
    background-color: #f36355;
  }

  &__checkbox:checked + &__track &__thumb {
    transform: translateX(16px);
  }

  &__checkbox:disabled + &__track {
    cursor: not-allowed;
  }

  &__label {
    font-weight: 400;
    font-size: 16px;
    line-height: 1.5;
    color: #39444c;
  }
}

.checkbox-labeled {
  @media (max-width: 640px) {
    flex-direction: row-reverse;
    justify-content: space-between;

    & label {
      margin: 0;
    }
  }
}

.summary {
  display: grid;
  gap: 44px;
  padding: 22px 0 0;

  &__person {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    color: #333f48;
    padding-bottom: 32px;
  }
}

@media (max-width: 640px) {
  .summary {
    &__person {
      font-size: 16px;
    }
  }
}

.checkout-checkbox {
  &:disabled {
    &:hover {
      border-color: rgba(#d1d8dd, 0.7);
      opacity: 0.7;
      cursor: not-allowed;
    }
  }
}

.faktura-error,
.faktura-error:focus {
  border: 2px solid #ff585d !important;

  @media (max-width: 480px) {
    border: 1px solid #ff585d !important;
  }
}
.error-text {
  visibility: hidden;

  &--is-visible {
    opacity: 1;
    visibility: visible;
  }
}
</style>
